import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  deleteAction,
  getActions,
  updateAction,
} from '../../redux/actions/actionActions'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { getProjetbyMission } from '../../redux/projets/projetActions'
import { NavLink, useNavigate } from 'react-router-dom'
import LaunchIcon from '@mui/icons-material/Launch'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import DeleteButton from '../../components/common/DeleteButton'
import PaginationComp from '../../components/common/pagination'
import UseAlert from '../../components/common/alert'
import EntitySelector from '../../components/common/EntitySelector'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import { hasRole } from '../../utils/checkRole'
import { UserRole } from '../../types/keycloak'
import { selectUsername } from '../../redux/keycloak/keycloakSelectors'
import { formatTitle } from '../../utils/formatTitle'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { formatCurrency } from '../../utils/formatNumbers'
import i18n from '../../i18n'
import { Action } from '../../types/actionTypes'
import {
  ListPageShell,
  listPageChipSx,
  listPageIconBtnStyle,
} from '../../components/common/ListPageShell'
import {
  priorityLabelsTranslations,
  TypeActionTranslations,
} from '../../utils/constants'
import '../../components/common/ListPageTable.css'
import TableEmptyState from '../../components/common/TableEmptyState'
import TableLoadingState from '../../components/common/TableLoadingState'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { ListPageDateCell } from '../../components/common/ListPageCells'
import { onClickableTableRow } from '../../utils/tableRowClick'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'
import { registerHeaderCount } from '../../utils/listCounts'
import { ImportTasksButton } from './import/ImportActionsWizard'

type TaskFilter = 'all' | 'in_progress' | 'unassigned' | 'overdue'

const PRIORITY_PILL: Record<number, { bg: string; color: string; label: string }> = {
  1: { bg: '#FAE6E3', color: '#96271B', label: 'Critical' },
  2: { bg: '#FAEFDA', color: '#84540A', label: 'High' },
  3: { bg: '#E6EFF8', color: '#22548A', label: 'Medium' },
  4: { bg: '#E1F4F0', color: '#076B5C', label: 'Low' },
}

const STATUS_PILL: Record<string, { bg: string; color: string }> = {
  'A faire': { bg: '#E1F4F0', color: '#076B5C' },
  'En cours': { bg: '#E6EFF8', color: '#22548A' },
  Terminé: { bg: '#E1F4F0', color: '#076B5C' },
  Suspendu: { bg: '#EAF0F0', color: '#5F7484' },
  'En attente': { bg: '#FAEFDA', color: '#84540A' },
}

function Pill({
  label,
  bg,
  color,
  showDot = true,
}: {
  label: string
  bg: string
  color: string
  showDot?: boolean
}) {
  return (
    <span className="incyia-pill" style={{ background: bg, color }}>
      {showDot && <span className="incyia-pill-dot" />}
      {label}
    </span>
  )
}

function DateCell({
  value,
  locale,
}: {
  value?: string | Date
  locale: string
}) {
  return <ListPageDateCell value={value} locale={locale} />
}

function isOverdue(action: Action): boolean {
  if (!action.DateFin) return false
  const status = (action.Status || '').trim().toLowerCase()
  if (status === 'terminé' || status === 'termine') return false
  const end = new Date(action.DateFin)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  return end < today
}

function isUnassigned(action: Action): boolean {
  return !action.ProjetId && !action.ProjetName
}

export default function ActionListe() {
  const { t } = useTranslation()
  const locale = i18n.language
  const currency = useSelector(selectSpecifications)[0]?.Currency
  const dispatch: AppDispatch = useAppDispatch()
  const navigate = useNavigate()
  const username = useSelector(selectUsername)
  const roles = useSelector((state: RootState) => state.auth.roles)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [search, setSearch] = useState('')
  const [taskFilter, setTaskFilter] = useState<TaskFilter>('all')
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [projectsMissionId, setProjectsMissionId] = useState<string | null>(
    null
  )
  const [activeSelectActionId, setActiveSelectActionId] = useState<
    string | null
  >(null)
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  const projetsList = useSelector(
    (state: RootState) => state.projet.ProjetResponse?.projets
  )
  const actionsList = useSelector(
    (state: RootState) => state.action.ActionResponse
  )
  const loading = useSelector((state: RootState) => state.action.loading)
  const showLoader = useTableLoadingGate(loading, [page, pageSize, search])

  useEffect(() => {
    dispatch(getActions({ page, pageSize, search }))
  }, [dispatch, page, pageSize, search])

  const filteredActions = useMemo(() => {
    const rows = actionsList?.actions ?? []
    if (taskFilter === 'all') return rows
    if (taskFilter === 'in_progress') {
      return rows.filter(
        (a) => (a.Status || '').trim().toLowerCase() === 'en cours'
      )
    }
    if (taskFilter === 'unassigned') {
      return rows.filter(isUnassigned)
    }
    return rows.filter(isOverdue)
  }, [actionsList?.actions, taskFilter])

  const projectsForActiveMission = useMemo(() => {
    if (!projectsMissionId || !projetsList?.length) return []
    return projetsList.filter((project) => {
      if (!project.MissionId) return true
      return String(project.MissionId) === String(projectsMissionId)
    })
  }, [projetsList, projectsMissionId])

  const canAdd =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.ADMIN) ||
    hasRole(roles, UserRole.SSI_TEAM)

  const canDelete =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM) ||
    hasRole(roles, UserRole.ADMIN)

  const fetchProjectsForAction = async (action: Action) => {
    if (!action.MissionId) {
      setProjectsMissionId(null)
      return
    }
    try {
      setActiveSelectActionId(action._id || null)
      setLoadingProjects(true)
      setProjectsMissionId(action.MissionId)
      await dispatch(
        getProjetbyMission({
          MissionId: action.MissionId,
          page: 1,
          pageSize: 200,
        })
      )
    } catch (error) {
      console.error('Error fetching projects:', error)
      setProjectsMissionId(null)
    } finally {
      setLoadingProjects(false)
    }
  }

  const handleProjectSelect = async (
    selectedProjectId: string,
    action: Action
  ) => {
    try {
      const selectedProject = projectsForActiveMission.find(
        (p) => p._id === selectedProjectId
      )
      if (
        selectedProject?.MissionId &&
        action.MissionId &&
        String(selectedProject.MissionId) !== String(action.MissionId)
      ) {
        setAlert({
          open: true,
          message: t('alerts.actionUpdate.projectMissionMismatch', {
            defaultValue:
              'This project belongs to another mission and cannot be linked.',
          }),
          severity: 'error',
        })
        return
      }

      const updatedAction: Action = {
        ...action,
        ProjetId: selectedProjectId,
        ProjetName: selectedProject?.ProjetName,
        MissionId: selectedProject?.MissionId || action.MissionId,
      }
      await dispatch(
        updateAction({
          updatedAction,
          _id: action._id || '',
        })
      )
      setAlert({
        open: true,
        message: t('alerts.actionUpdate.success'),
        severity: 'success',
      })
      dispatch(getActions({ search, page, pageSize }))
    } catch (error) {
      console.error('Error updating action:', error)
      setAlert({
        open: true,
        message: t('Error updating action'),
        severity: 'error',
      })
    }
  }

  const handleDeleteClick = async (actionId: string) => {
    try {
      await dispatch(deleteAction(actionId))
      setAlert({
        open: true,
        message: t('alerts.actionDelete.success'),
        severity: 'success',
      })
    } catch (error) {
      console.error('Error deleting action:', error)
      setAlert({
        open: true,
        message: t('alerts.actionDelete.error'),
        severity: 'error',
      })
    }
  }

  const canEditRow = (action: Action) =>
    username?.toLocaleLowerCase() ===
      action?.CreatedBy?.toLocaleLowerCase() ||
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.ADMIN)

  const filterChips: { key: TaskFilter; label: string }[] = [
    { key: 'all', label: t('listPages.all') },
    { key: 'in_progress', label: t('listPages.inProgress') },
    { key: 'unassigned', label: t('listPages.unassignedProject') },
    { key: 'overdue', label: t('listPages.overdue') },
  ]

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert({ ...alert, open: false })}
      />

      <ListPageShell
        eyebrow={t('managment')}
        title={t('tasks')}
        titleTourId="actions-page-header"
        subtitle={t('listPages.tasksSub')}
        addLabel={canAdd ? t('buttons.addTask') : undefined}
        onAdd={canAdd ? () => navigate('/addaction') : undefined}
        headActions={
          canAdd ? (
            <ImportTasksButton
              onImported={(created, failed) => {
                dispatch(getActions({ search, page, pageSize }))
                setAlert({
                  open: true,
                  message: failed
                    ? t('actionImport.alerts.partial', { created, failed })
                    : t('actionImport.alerts.success', { count: created }),
                  severity: failed ? 'warning' : 'success',
                })
              }}
            />
          ) : undefined
        }
        search={search}
        onSearchChange={(value) => {
          setSearch(value)
          setPage(1)
        }}
        searchPlaceholder={t('filterByName')}
        countLabel={t('listPages.tasksCount', {
          count: registerHeaderCount(
            actionsList?.total ?? 0,
            filteredActions.length,
            taskFilter === 'all'
          ),
        })}
        chips={
          <>
            {filterChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                style={listPageChipSx(taskFilter === chip.key)}
                onClick={() => setTaskFilter(chip.key)}
              >
                {chip.label}
              </button>
            ))}
          </>
        }
        footer={
          (actionsList?.totalPages as number) > 0 && taskFilter === 'all' ? (
            <PaginationComp
              page={page}
              pageSize={pageSize}
              onChange={(nextPage, newSize) => {
                setPage(nextPage)
                setPageSize(newSize)
              }}
              totalItems={actionsList?.totalPages as number}
            />
          ) : undefined
        }
      >
        <div className="incyia-table-wrap">
          {showLoader ? (
            <TableLoadingState />
          ) : filteredActions.length === 0 ? (
            <TableEmptyState />
          ) : (
            <table className="incyia-table">
              <thead>
                <tr>
                  <th>{t('task_name')}</th>
                  <th>{t('Project Name')}</th>
                  <th>{t('Description')}</th>
                  <th>{t('Start Date')}</th>
                  <th>{t('End Date')}</th>
                  <th>{t('Priority')}</th>
                  <th>{t('task_type')}</th>
                  <th>{t('Status')}</th>
                  <th className="incyia-num">{t('cost')}</th>
                  <th className="incyia-num">{t('total_cost')}</th>
                  <th>{t('Mission Name')}</th>
                  <th style={{ textAlign: 'right' }}>{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredActions.map((action: Action) => {
                  const priority = Number(action.Priority)
                  const priorityStyle =
                    PRIORITY_PILL[priority] ?? PRIORITY_PILL[4]
                  const statusStyle = STATUS_PILL[action.Status || ''] ?? {
                    bg: '#EAF0F0',
                    color: '#5F7484',
                  }
                  const typeLabel =
                    TypeActionTranslations[action.TypeAction || ''] ||
                    action.TypeAction ||
                    '—'

                  return (
                    <tr key={action._id} onClick={onClickableTableRow}>
                      <td className="incyia-col-pinned">
                        <NavLink
                          to={`/actiond/${action._id}`}
                          className="incyia-name-link"
                        >
                          <ListPageDescription
                            value={
                              action.ActionName
                                ? formatTitle(action.ActionName)
                                : undefined
                            }
                            tone="primary"
                          />
                        </NavLink>
                      </td>
                      <td>
                        <EntitySelector
                          value={action.ProjetName}
                          linkTo={`/mission/${action.MissionId}/projet/${action.ProjetId}`}
                          rowData={action}
                          options={
                            loadingProjects &&
                            activeSelectActionId === action._id
                              ? []
                              : projectsMissionId &&
                                  action.MissionId &&
                                  String(projectsMissionId) ===
                                    String(action.MissionId)
                                ? projectsForActiveMission
                                : []
                          }
                          optionLabelKey="ProjetName"
                          placeholder={
                            action.MissionId
                              ? t('listPages.linkProject', {
                                  defaultValue: 'Link project',
                                })
                              : t('entitySelector.noMission', {
                                  defaultValue: 'No mission',
                                })
                          }
                          entityName="project"
                          onSelect={handleProjectSelect}
                          onFetchOptions={fetchProjectsForAction}
                          isLoading={
                            loadingProjects &&
                            activeSelectActionId === action._id
                          }
                          disabled={!action.MissionId}
                        />
                      </td>
                      <td className="incyia-wrap">
                        <ListPageDescription value={action.Description} />
                      </td>
                      <td>
                        <DateCell value={action.DateDebut} locale={locale} />
                      </td>
                      <td>
                        <DateCell value={action.DateFin} locale={locale} />
                      </td>
                      <td>
                        <Pill
                          label={
                            t(`priority.${priority}`, {
                              defaultValue:
                                priorityLabelsTranslations[priority] ||
                                priorityStyle.label,
                            }) || priorityStyle.label
                          }
                          bg={priorityStyle.bg}
                          color={priorityStyle.color}
                        />
                      </td>
                      <td>
                        <Pill
                          label={t(typeLabel, { defaultValue: typeLabel })}
                          bg="#EAF0F0"
                          color="#5F7484"
                          showDot={false}
                        />
                      </td>
                      <td>
                        <Pill
                          label={t(action.Status || '', {
                            defaultValue: action.Status || '—',
                          })}
                          bg={statusStyle.bg}
                          color={statusStyle.color}
                        />
                      </td>
                      <td>
                        <span className="incyia-money">
                          {formatCurrency(action.Cout ?? 0, currency, {
                            locale,
                            useIntl: true,
                          })}
                        </span>
                      </td>
                      <td>
                        <span className="incyia-money">
                          {formatCurrency(action.CoutTotal ?? 0, currency, {
                            locale,
                            useIntl: true,
                          })}
                        </span>
                      </td>
                      <td className="incyia-muted">
                        <ListPageDescription value={action.MissionName} />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="incyia-row-actions">
                          <button
                            type="button"
                            title={t('listPages.openAction')}
                            onClick={() => navigate(`/actiond/${action._id}`)}
                            style={listPageIconBtnStyle}
                          >
                            <LaunchIcon sx={{ fontSize: 15 }} />
                          </button>
                          {canEditRow(action) && (
                            <button
                              type="button"
                              title={t('listPages.edit')}
                              onClick={() =>
                                navigate(`/action/${action._id}`)
                              }
                              style={listPageIconBtnStyle}
                            >
                              <DriveFileRenameOutlineOutlinedIcon
                                sx={{ fontSize: 15 }}
                              />
                            </button>
                          )}
                          {canDelete && (
                            <DeleteButton
                              onDelete={() =>
                                handleDeleteClick(action._id as string)
                              }
                              item={{
                                entityType: t('tasks', {
                                  defaultValue: 'Task',
                                }),
                                name: action.ActionName
                                  ? formatTitle(action.ActionName)
                                  : undefined,
                                details: [
                                  {
                                    label: t('deleteConfirm.fields.status'),
                                    value: action.Status || '—',
                                  },
                                  {
                                    label: t('deleteConfirm.fields.priority'),
                                    value:
                                      PRIORITY_PILL[Number(action.Priority)]
                                        ?.label || String(action.Priority),
                                  },
                                  {
                                    label: t('deleteConfirm.fields.mission'),
                                    value: action.MissionName || '—',
                                  },
                                  {
                                    label: t('deleteConfirm.fields.project'),
                                    value: action.ProjetName || '—',
                                  },
                                ],
                              }}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </ListPageShell>
    </>
  )
}
